import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControlLabel,
  Checkbox,
  Switch,
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GetAppIcon from "@material-ui/icons/GetApp";
import NotificationContext from "views/Components/Context.js";
import {
  fetchNotebookAPI,
  submitLabForGenerationAPI,
  replaceNotebookAPI,
} from "views/APIs/APIUtility";
import { IpynbRenderer } from "react-ipynb-renderer-katex";
import "katex/dist/katex.min.css";
import "react-ipynb-renderer-katex/dist/styles/oceans16.css";

import Button from "components/CustomButtons/Button.js";
import { DropzoneArea } from "material-ui-dropzone";
import Alert from "views/Components/Alert";
import { Public, SkipNext } from "@material-ui/icons";
import { makeNotebookPublicAPI } from "views/APIs/APIUtility";
import { skipStreamlitGenerationAPI } from "views/APIs/APIUtility";
import { generateStreamlitRequirementsForConceptLab } from "views/APIs/LabsUtility";

const useStyles = makeStyles((theme) => ({
  container: { padding: theme.spacing(3) },
  buttonBox: { marginTop: theme.spacing(2), textAlign: "right" },
  iconBtn: { marginRight: theme.spacing(1) },
}));

function NotebookViewer({ html }) {
  const box = useRef(null);

  /* load MathJax exactly once */
  useEffect(() => {
    if (window.MathJax) return; // already loaded

    const s = document.createElement("script");
    s.id = "mathjax";
    s.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js";
    s.async = true;
    s.onload = () => {
      if (box.current) window.MathJax.typesetPromise([box.current]);
    };
    document.head.appendChild(s);
  }, []);

  /* run MathJax every time the HTML changes */
  useEffect(() => {
    if (window.MathJax && box.current) {
      window.MathJax.typesetPromise([box.current]);
    }
  }, [html]);

  return (
    <div
      ref={box}
      dangerouslySetInnerHTML={{ __html: html }}
      style={{ width: "100%", overflow: "auto" }}
    />
  );
}

export default function NotebookReview({ labID, lab }) {
  const classes = useStyles();
  const { showNotification } = useContext(NotificationContext);
  const [loading, setLoading] = useState({
    isLoading: true,
    message: "Loading Notebook...",
  });
  const [colab_content, setColabContent] = useState(null);
  const [nbUrl, setNbUrl] = useState("");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [colabUrl, setColabUrl] = useState("");
  const [s3_content, setS3Content] = useState(null);

  const loadNotebook = async () => {
    setLoading({ isLoading: true, message: "Loading Notebook..." });
    try {
      if (labID === null) {
        return;
      }
      const data = await fetchNotebookAPI(labID);
      if (data === null || data === undefined) {
        setLoading({ isLoading: false, message: "" });
        showNotification("Failed to load notebook", "danger");
        return;
      }
      if (
        (data.s3_content === null || data.s3_content === undefined) &&
        (data.colab_content === null || data.colab_content === undefined)
      ) {
        showNotification("Failed to load notebook", "danger");
        setLoading({ isLoading: false, message: "" });
        return;
      }
      setColabContent(data.colab_content);
      setNbUrl(data.notebook_url);
      setColabUrl(data.colab_url);
      setS3Content(data.s3_content);
    } catch (err) {
      console.error(err);
      showNotification("Failed to load notebook", "danger");
      setLoading({ isLoading: false, message: "" });
    } finally {
      setLoading({ isLoading: false, message: "" });
    }
  };

  useEffect(() => {
    loadNotebook();
  }, [labID]);

  const handleDownload = () => {
    if (!nbUrl) return;
    const url = `${nbUrl}?response-content-disposition=attachment`;

    const a = document.createElement("a");
    a.href = url;
    a.download = "notebook.ipynb"; // ignored in Chrome if cross-origin, but harmless
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleReplace = async () => {
    if (!file) {
      showNotification("Please choose a file", "warning");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("lab_id", labID);
    fd.append("notebook_file", file);

    try {
      await replaceNotebookAPI(fd);
      setDlgOpen(false);
      setFile(null);
      await loadNotebook(); // refresh viewer
      showNotification("Notebook replaced", "success");
    } catch (err) {
      console.error(err);
      showNotification(err.message, "danger");
    } finally {
      setBusy(false);
    }
  };

  const [submitDlgOpen, setSubmitDlgOpen] = useState(false);
  const [queueDlgOpen, setQueueDlgOpen] = useState(false);
  const [replaceDlgOpen, setReplaceDlgOpen] = useState(false);

  const handleCompleteClick = () => {
    const status = lab.status; // or use labStatus prop
    if (status === "In Lab Generation Queue") {
      setQueueDlgOpen(true); // open “already in queue” dialog
    } else {
      setSubmitDlgOpen(true); // open standard submit/edit dialog
    }
  };

  const proceedFromQueueDialog = () => {
    setQueueDlgOpen(false);
    handleSubmit();
  };

  const handleSubmit = async () => {
    setSubmitDlgOpen(false);
    setLoading({
      isLoading: true,
      message: "Generating Streamlit Requirements...",
    });
    const formData = new FormData();
    formData.append("lab_id", labID);
    await generateStreamlitRequirementsForConceptLab(formData);
    window.location.reload();
    setLoading({ isLoading: false, message: "" });
  };

  const container = useRef(null);

  // 1. Load MathJax v3 once, if it is not already on the page
  useEffect(() => {
    if (window.MathJax) return; // already loaded

    const tag = document.createElement("script");
    tag.id = "mathjax";
    tag.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js";
    tag.async = true;
    tag.onload = () => {
      // First render once the library finishes downloading
      if (container.current) {
        window.MathJax.typesetPromise([container.current]);
      }
    };
    document.head.appendChild(tag);
  }, []);

  // 2. Re-typeset whenever the notebook HTML changes
  useEffect(() => {
    if (window.MathJax && container.current) {
      window.MathJax.typesetPromise([container.current]);
    }
  }, [colab_content]);

  const [makePublic, setMakePublic] = useState(lab?.colab_public || false);

  const handleToggleVisibility = async (value) => {
    setBusy(true);
    const formData = new FormData();
    setMakePublic(value);
    formData.append("lab_id", labID);
    formData.append("make_public", value);
    await makeNotebookPublicAPI(formData);
    showNotification("Notebook visibility changed", "success");
  };

  const handleSkipStreamlitGeneration = async () => {
    setBusy(true);
    const formData = new FormData();
    formData.append("lab_id", labID);
    await skipStreamlitGenerationAPI(formData);
    showNotification("Streamlit generation skipped", "success");
    window.location.reload();
  };

  if (loading.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress className={classes.loaderClass} />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }} /* optional margin-bottom */
      >
        {/* Left section */}
        <Box>
          <Typography variant="h5">Review Notebook</Typography>
          <Typography variant="body1">
            Review the generated Notebook. You can download and replace the
            notebook if you need to make changes.
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center" // vertically center contents
        >
          {/* <Button
                        color="transparent"
                        startIcon={<Public />}
                        onClick={handleMakePublic}
                        disabled={busy}
                        sx={{ height: 40 }}   // pick a height; 40 px is the default for MUI contained buttons
                    >
                        Make Notebook Public
                    </Button> */}
          {/* create a toggle */}
          <Typography variant="body2">Private</Typography>
          <Switch
            checked={makePublic}
            onChange={(e) => handleToggleVisibility(e.target.checked)}
            color="primary"
            name="makePublic"
            size="small"
          />
          <Typography variant="body2">Public</Typography>

          {/* Right button shown only when a colab URL exists */}
          {colabUrl && (
            <Button
              component="a" // render as an anchor
              href={colabUrl}
              target="_blank"
              color="transparent"
              rel="noopener noreferrer"
              sx={{
                p: 0, // remove padding, let the badge fill the button
                minWidth: 120, // keeps a reasonable width
                height: 50, // same as the left button
              }}
            >
              <img
                src="https://colab.research.google.com/assets/colab-badge.svg"
                alt="Open in Colab"
                style={{ height: "100%" }} // make the badge fill the button height
              />
            </Button>
          )}
        </Box>
      </Box>

      <Divider style={{ marginTop: "16px", marginBottom: "16px" }} />

      <Box
        /* wrapper for scrolling */
        style={{
          position: "relative", // prevents the big notebook height from pushing layout
          maxHeight: 600, // or '60vh' / '30rem' – adjust as you like
          overflowY: "auto",
          overflowX: "hidden",
          paddingRight: 8, // optional: leaves room for scrollbar
        }}
      >
        {colab_content ? (
          <NotebookViewer html={colab_content} />
        ) : s3_content ? (
          <IpynbRenderer ipynb={s3_content} />
        ) : (
          <Typography variant="body1" style={{ marginBottom: "16px" }}>
            Notebook not found
          </Typography>
        )}
      </Box>

      <Divider style={{ marginTop: "16px", marginBottom: "16px" }} />

      <Box className={classes.buttonBox}>
        {/* <Button
                    color="quCreateColor"
                    startIcon={<GetAppIcon />}
                    onClick={handleDownload}
                    disabled={!nbUrl}
                >
                    Download
                </Button> */}

        <Button
          color="quCreateColor"
          startIcon={<CloudUploadIcon />}
          onClick={() => setDlgOpen(true)}
        >
          Replace
        </Button>

        <Button
          color="quCreateColor"
          endIcon={<SkipNext />}
          onClick={handleSkipStreamlitGeneration}
          disabled={busy}
          sx={{ height: 40 }} // pick a height; 40 px is the default for MUI contained buttons
        >
          Skip Streamlit Generation
        </Button>

        <Button
          color="quCreateColor"
          endIcon={<NavigateNextIcon />}
          onClick={handleCompleteClick}
        >
          Generate Streamlit Lab
        </Button>
      </Box>

      <Dialog
        open={dlgOpen}
        onClose={() => !busy && setDlgOpen(false)}
        fullWidth
      >
        <DialogTitle>Replace Notebook</DialogTitle>

        <DialogContent dividers>
          {/* Dropzone – only .ipynb, one file */}
          <DropzoneArea
            acceptedFiles={[".ipynb"]}
            filesLimit={1}
            showPreviews={true}
            showPreviewsInDropzone={false}
            maxFileSize={20 * 1024 * 1024} // 20 MB cap (adjust as needed)
            dropzoneText="Drag & drop the updated .ipynb file here or click"
            onChange={(files) => setFile(files[0] || null)}
            showAlerts={["error"]}
            useChipsForPreview
          />
        </DialogContent>

        <DialogActions>
          <Button
            color="quCreateColor"
            onClick={() => setDlgOpen(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            color="quCreateColor"
            onClick={handleReplace}
            disabled={busy || !file}
            startIcon={<CloudUploadIcon />}
          >
            {busy ? (
              <CircularProgress size={18} className={classes.loaderClass} />
            ) : (
              "Upload"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={replaceDlgOpen}
        onClose={() => !busy && setReplaceDlgOpen(false)}
        fullWidth
      >
        <DialogTitle>Replace Notebook</DialogTitle>
        <DialogContent dividers>
          <DropzoneArea
            acceptedFiles={[".ipynb"]}
            filesLimit={1}
            showPreviewsInDropzone={false}
            maxFileSize={20 * 1024 * 1024}
            dropzoneText="Drag & drop the updated .ipynb file here or click"
            onChange={(files) => setFile(files[0] || null)}
            showAlerts={["error"]}
            useChipsForPreview
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="quCreateColor"
            onClick={() => setReplaceDlgOpen(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            color="quCreateColor"
            onClick={handleReplace}
            disabled={busy || !file}
            startIcon={<CloudUploadIcon />}
          >
            {busy ? (
              <CircularProgress size={18} className={classes.loaderClass} />
            ) : (
              "Upload"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- Queue-exists dialog ---------------- */}
      {queueDlgOpen && (
        <Alert
          title="Are you sure?"
          warning={true}
          showCancel={true}
          cancelBtnText="No"
          confirmBtnText="Yes"
          onCancel={() => setQueueDlgOpen(false)}
          onConfirm={proceedFromQueueDialog}
        >
          Proceeding to the next step will reset your current progress in
          further steps. Your existing work will be overwritten, and new content
          will be created. Are you sure you want to continue?
        </Alert>
      )}

      {/* ---------------- Submit-or-edit dialog ---------------- */}
      <Dialog open={submitDlgOpen} onClose={() => setSubmitDlgOpen(false)}>
        <DialogTitle>Submit for generation?</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Do you want to submit this notebook for the application generation
            now, or continue editing?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="quCreateColor" onClick={() => setSubmitDlgOpen(false)}>
            Continue Editing
          </Button>
          <Button
            color="quCreateColor"
            onClick={handleSubmit}
            endIcon={<NavigateNextIcon />}
            autoFocus
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
